/**
 * Dreamactor V2 Test Page - Secret test page for puppet-style 2D animation
 * 
 * Motion transfer from a driving video to a static 2D character image.
 * Creates consistent character animation without morphing - perfect for 2D cartoons.
 * Access via: /dreamactor-test (secret, unlisted route)
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Zap, Loader2, Image as ImageIcon, Film,
  Upload, Trash2, Play, Pause,
  Sparkles, Check, X, AlertTriangle, Settings2,
  Download, Video, ArrowLeft, Eye, EyeOff, Info, Wand2
} from "lucide-react";
import api from "@/config/axiosInstance";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getAssetUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${API}${url}`;
  return url;
};

export default function DreamactorTest() {
  const navigate = useNavigate();
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [targetImageUrl, setTargetImageUrl] = useState("");
  const [sourceVideoUrl, setSourceVideoUrl] = useState("");
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState(5);
  const [fps, setFps] = useState(24);
  const [guidanceScale, setGuidanceScale] = useState(7.5);
  const [inferenceSteps, setInferenceSteps] = useState(50);
  
  // Upload refs
  const targetImageRef = useRef(null);
  const sourceVideoRef = useRef(null);
  
  // Video preview state
  const [videoPreviewPlaying, setVideoPreviewPlaying] = useState(false);
  const videoPreviewRef = useRef(null);
  
  // Generation state
  const [generating, setGenerating] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const pollRef = useRef(null);

  useEffect(() => {
    loadConfig();
    loadJobHistory();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get("/dreamactor-test/config");
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to load config:", error);
      toast.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const loadJobHistory = async () => {
    try {
      const response = await api.get("/dreamactor-test/jobs?limit=10");
      setJobHistory(response.data.jobs || []);
    } catch (error) {
      console.error("Failed to load job history:", error);
    }
  };

  const uploadFile = async (file, type = "image") => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await api.post("/uploads/temp", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data.url;
    } catch (error) {
      console.error("Upload error:", error);
      return URL.createObjectURL(file);
    }
  };

  const handleTargetImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    toast.loading("Uploading character image...");
    const url = await uploadFile(file, "image");
    setTargetImageUrl(url);
    toast.dismiss();
    toast.success("Character image uploaded");
  };

  const handleSourceVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    toast.loading("Uploading driving video...");
    const url = await uploadFile(file, "video");
    setSourceVideoUrl(url);
    toast.dismiss();
    toast.success("Driving video uploaded");
  };

  const toggleVideoPreview = () => {
    if (videoPreviewRef.current) {
      if (videoPreviewPlaying) {
        videoPreviewRef.current.pause();
      } else {
        videoPreviewRef.current.play();
      }
      setVideoPreviewPlaying(!videoPreviewPlaying);
    }
  };

  const handleGenerate = async () => {
    if (!targetImageUrl) {
      toast.error("Please upload a character image");
      return;
    }
    if (!sourceVideoUrl) {
      toast.error("Please upload a driving video");
      return;
    }
    
    setGenerating(true);
    setJobStatus(null);
    
    try {
      const response = await api.post("/dreamactor-test/generate", {
        target_image_url: targetImageUrl,
        source_video_url: sourceVideoUrl,
        prompt: prompt.trim() || null,
        duration,
        fps,
        guidance_scale: guidanceScale,
        num_inference_steps: inferenceSteps
      });
      
      if (response.data.success) {
        toast.success("Motion transfer started!");
        startPolling(response.data.job_id);
      } else {
        throw new Error(response.data.message || "Failed to start generation");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to start generation");
      setGenerating(false);
    }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    
    const poll = async () => {
      try {
        const response = await api.get(`/dreamactor-test/status/${jobId}`);
        setJobStatus(response.data);
        
        if (response.data.status === "completed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          toast.success("Motion transfer complete!");
          loadJobHistory();
        } else if (response.data.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          toast.error(response.data.error_message || "Motion transfer failed");
          loadJobHistory();
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    };
    
    poll();
    pollRef.current = setInterval(poll, 3000);
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await api.delete(`/dreamactor-test/jobs/${jobId}`);
      setJobHistory(prev => prev.filter(j => j.job_id !== jobId));
      toast.success("Job deleted");
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const handleDownloadVideo = async (videoUrl, jobId) => {
    try {
      const response = await fetch(getAssetUrl(videoUrl));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dreamactor_${jobId.slice(0, 8)}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error("Failed to download video");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-zinc-950 text-white">
        {/* Header */}
        <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="text-zinc-400 hover:text-white"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-purple-400" />
                <h1 className="text-lg font-semibold">Dreamactor V2 Test Lab</h1>
                <Badge variant="outline" className="text-purple-400 border-purple-400/50 text-xs">
                  Secret
                </Badge>
              </div>
            </div>
            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">
              Puppet Animation
            </Badge>
          </div>
        </header>

        <main className="max-w-7xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info Banner */}
            <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-purple-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-zinc-300 space-y-1">
                    <p className="font-medium text-purple-300">Puppet-Style 2D Animation</p>
                    <p className="text-zinc-400 text-xs">
                      Upload a static character image and a driving video. The character will perform 
                      the motion from the video while maintaining its exact appearance - no morphing!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Input Section - Character + Motion */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Target Character Image */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-purple-400" />
                    Character Image *
                  </CardTitle>
                  <CardDescription className="text-xs text-zinc-500">
                    Your 2D character to animate
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div 
                    className="aspect-square rounded-lg border border-dashed border-zinc-700 bg-zinc-800/50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-purple-500/50 transition-colors"
                    onClick={() => targetImageRef.current?.click()}
                    data-testid="target-image-upload"
                  >
                    {targetImageUrl ? (
                      <img src={getAssetUrl(targetImageUrl)} alt="Character" className="w-full h-full object-contain" />
                    ) : (
                      <div className="text-center p-4">
                        <Upload className="w-8 h-8 mx-auto text-zinc-500 mb-2" />
                        <p className="text-sm text-zinc-500">Click to upload</p>
                        <p className="text-xs text-zinc-600">PNG, JPG (512x512+ recommended)</p>
                      </div>
                    )}
                  </div>
                  <input 
                    ref={targetImageRef} 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handleTargetImageUpload} 
                  />
                  {targetImageUrl && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full mt-2 text-xs text-zinc-400 hover:text-red-400" 
                      onClick={() => setTargetImageUrl("")}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Source Driving Video */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Film className="w-4 h-4 text-purple-400" />
                    Driving Video *
                  </CardTitle>
                  <CardDescription className="text-xs text-zinc-500">
                    Motion source (dance, walk, gesture)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div 
                    className="aspect-square rounded-lg border border-dashed border-zinc-700 bg-zinc-800/50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-purple-500/50 transition-colors relative"
                    onClick={() => !sourceVideoUrl && sourceVideoRef.current?.click()}
                    data-testid="source-video-upload"
                  >
                    {sourceVideoUrl ? (
                      <>
                        <video 
                          ref={videoPreviewRef}
                          src={getAssetUrl(sourceVideoUrl)} 
                          className="w-full h-full object-contain"
                          loop
                          muted
                          onPlay={() => setVideoPreviewPlaying(true)}
                          onPause={() => setVideoPreviewPlaying(false)}
                        />
                        <Button
                          variant="secondary"
                          size="icon"
                          className="absolute bottom-2 right-2 h-8 w-8 bg-black/70 hover:bg-black/90"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleVideoPreview();
                          }}
                        >
                          {videoPreviewPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </Button>
                      </>
                    ) : (
                      <div className="text-center p-4">
                        <Upload className="w-8 h-8 mx-auto text-zinc-500 mb-2" />
                        <p className="text-sm text-zinc-500">Click to upload</p>
                        <p className="text-xs text-zinc-600">MP4, MOV (1-30 sec)</p>
                      </div>
                    )}
                  </div>
                  <input 
                    ref={sourceVideoRef} 
                    type="file" 
                    accept="video/*" 
                    className="hidden" 
                    onChange={handleSourceVideoUpload} 
                  />
                  {sourceVideoUrl && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full mt-2 text-xs text-zinc-400 hover:text-red-400" 
                      onClick={() => {
                        setSourceVideoUrl("");
                        setVideoPreviewPlaying(false);
                      }}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Optional Prompt */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  Style Prompt (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Optional: Describe the style, lighting, or scene... e.g., 'Cartoon character dancing in a colorful stage, vibrant lighting'"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="min-h-[80px] bg-zinc-800/50 border-zinc-700 text-sm"
                  disabled={generating}
                  data-testid="style-prompt-input"
                />
              </CardContent>
            </Card>

            {/* Generation Settings */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-purple-400" />
                    Generation Settings
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="text-xs text-zinc-400"
                  >
                    {showAdvanced ? "Hide" : "Show"} Advanced
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Duration */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-zinc-400">Duration</Label>
                    <span className="text-xs font-medium text-purple-400">{duration}s</span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {[5, 10, 15, 20, 30].map(d => (
                      <Button
                        key={d}
                        variant={duration === d ? "default" : "outline"}
                        size="sm"
                        className={duration === d ? "bg-purple-600 hover:bg-purple-700" : "border-zinc-700"}
                        onClick={() => setDuration(d)}
                        disabled={generating}
                      >
                        {d}s
                      </Button>
                    ))}
                  </div>
                </div>

                {/* FPS */}
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Frame Rate (FPS)</Label>
                  <Select value={fps.toString()} onValueChange={(v) => setFps(parseInt(v))} disabled={generating}>
                    <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24">24 FPS (Cinematic)</SelectItem>
                      <SelectItem value="30">30 FPS (Smooth)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Advanced Settings */}
                {showAdvanced && (
                  <div className="space-y-4 pt-2 border-t border-zinc-800">
                    {/* Guidance Scale */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400 flex items-center gap-1">
                          Guidance Scale
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="w-3 h-3" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">Higher = more adherence to prompt (5-20)</p>
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                        <span className="text-xs font-medium text-purple-400">{guidanceScale}</span>
                      </div>
                      <Slider
                        value={[guidanceScale]}
                        onValueChange={([v]) => setGuidanceScale(v)}
                        min={1}
                        max={20}
                        step={0.5}
                        disabled={generating}
                        className="py-2"
                      />
                    </div>

                    {/* Inference Steps */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400 flex items-center gap-1">
                          Quality (Steps)
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="w-3 h-3" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">More steps = better quality, slower (20-100)</p>
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                        <span className="text-xs font-medium text-purple-400">{inferenceSteps}</span>
                      </div>
                      <Slider
                        value={[inferenceSteps]}
                        onValueChange={([v]) => setInferenceSteps(v)}
                        min={20}
                        max={100}
                        step={5}
                        disabled={generating}
                        className="py-2"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={generating || !targetImageUrl || !sourceVideoUrl}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg font-medium"
              data-testid="generate-dreamactor-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Transferring Motion...
                </>
              ) : (
                <>
                  <Wand2 className="w-5 h-5 mr-2" />
                  Animate Character
                </>
              )}
            </Button>

            {/* Job Status */}
            {jobStatus && (
              <Card className={`border ${
                jobStatus.status === "completed" ? "border-green-500/50 bg-green-500/5" :
                jobStatus.status === "failed" ? "border-red-500/50 bg-red-500/5" :
                "border-purple-500/50 bg-purple-500/5"
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {jobStatus.status === "completed" ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : jobStatus.status === "failed" ? (
                        <X className="w-4 h-4 text-red-400" />
                      ) : (
                        <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                      )}
                      <span className="text-sm font-medium capitalize">{jobStatus.status}</span>
                    </div>
                    <span className="text-xs text-zinc-500">{Math.round(jobStatus.progress_percent)}%</span>
                  </div>
                  
                  <Progress value={jobStatus.progress_percent} className="h-2 mb-2" />
                  <p className="text-xs text-zinc-400">{jobStatus.progress_message}</p>
                  
                  {jobStatus.status === "completed" && jobStatus.video_url && (
                    <div className="mt-4">
                      <video src={getAssetUrl(jobStatus.video_url)} controls className="w-full rounded-lg bg-black" />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2 text-xs border-green-500/50 text-green-400 hover:bg-green-500/10"
                        onClick={() => handleDownloadVideo(jobStatus.video_url, jobStatus.job_id)}
                        data-testid="download-result-btn"
                      >
                        <Download className="w-3 h-3 mr-1" /> Download Animation
                      </Button>
                    </div>
                  )}
                  
                  {jobStatus.status === "failed" && jobStatus.error_message && (
                    <div className="mt-2 p-2 bg-red-500/10 rounded text-xs text-red-400">
                      {jobStatus.error_message}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - History & Tips */}
          <div className="space-y-4">
            {/* History */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Film className="w-4 h-4 text-purple-400" />
                    History
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)} className="h-6 w-6 p-0">
                    {showHistory ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </CardHeader>
              {showHistory && (
                <CardContent className="p-0">
                  <ScrollArea className="h-[400px]">
                    {jobHistory.length === 0 ? (
                      <div className="p-4 text-center text-zinc-500 text-sm">No animations yet</div>
                    ) : (
                      <div className="divide-y divide-zinc-800">
                        {jobHistory.map((job) => (
                          <div key={job.job_id} className="p-3 hover:bg-zinc-800/30 transition-colors">
                            <div className="flex items-start gap-2 mb-2">
                              <Badge variant="outline" className={`text-[10px] ${
                                job.status === "completed" ? "text-green-400 border-green-500/50" :
                                job.status === "failed" ? "text-red-400 border-red-500/50" :
                                "text-purple-400 border-purple-500/50"
                              }`}>
                                {job.status}
                              </Badge>
                              <span className="text-[10px] text-zinc-500">
                                {new Date(job.created_at).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mb-2 text-xs text-zinc-400">
                              <span>{job.duration}s</span>
                              <span>•</span>
                              <span>{job.fps} FPS</span>
                            </div>
                            {job.status === "completed" && job.video_url && (
                              <video src={getAssetUrl(job.video_url)} controls className="w-full rounded-lg bg-black mb-2" />
                            )}
                            <div className="flex gap-2">
                              {job.status === "completed" && job.video_url && (
                                <Button variant="ghost" size="sm" className="flex-1 text-[10px] h-7 text-green-400" onClick={() => handleDownloadVideo(job.video_url, job.job_id)}>
                                  <Download className="w-3 h-3 mr-1" /> Download
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" className="text-[10px] h-7 text-red-400" onClick={() => handleDeleteJob(job.job_id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              )}
            </Card>

            {/* How It Works */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Info className="w-4 h-4 text-purple-400" />
                  How It Works
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-zinc-500 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">1</span>
                  <p>Upload your <strong className="text-purple-400">2D character</strong> image</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">2</span>
                  <p>Upload a <strong className="text-purple-400">driving video</strong> with the motion you want</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">3</span>
                  <p>The AI transfers the motion to your character - <strong className="text-green-400">no morphing!</strong></p>
                </div>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  Tips for Best Results
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-zinc-500 space-y-1">
                <p>• Use <strong className="text-purple-400">high-res character images</strong> (512x512+)</p>
                <p>• <strong className="text-purple-400">Front-facing</strong> characters work best</p>
                <p>• <strong className="text-purple-400">Clean background</strong> on driving video helps</p>
                <p>• Keep driving video <strong className="text-purple-400">1-10 seconds</strong> for best quality</p>
                <p>• Increase steps for <strong className="text-purple-400">smoother animation</strong></p>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
